/**
 * Frontend utilities for working with roles.
 *
 * Roles now live in the `roles` table (see migration 20260624205534). Display
 * data (name, color, sort order) is resolved at runtime through the RolesProvider
 * / useRoles() context. This module holds the pure, context-free helpers that the
 * context (and any non-React code holding a role record) builds on.
 */

import type { CSSProperties } from "react";
import type { Database } from "../types/database.types";

export type Role = Database["public"]["Tables"]["roles"]["Row"];

/** A role id (uuid). Employees hold an array of these; assignments hold one. */
export type RoleId = string;

/**
 * Normalizes a role value to an array. Handles both array and single value
 * (e.g. from a view column or during data normalization).
 */
export function normalizeRoleToArray(
  role: RoleId[] | RoleId | null | undefined
): RoleId[] {
  if (role == null) return [];
  return Array.isArray(role) ? role : [role];
}

/**
 * Resolves role id(s) to their display names, falling back to the raw id when a
 * role is unknown (or roles haven't loaded). Used to record audit-log changes by
 * name at write time, so the log is readable without a runtime id→name lookup.
 */
export function roleIdsToNames(
  ids: RoleId[] | RoleId | null | undefined,
  formatRole: (id: string | null | undefined) => string,
): string[] {
  return normalizeRoleToArray(ids).map((id) => formatRole(id) || id);
}

/**
 * Converts a hex color (e.g. "#1d4ed8") to an rgba() string at the given alpha.
 * Used to derive the tinted badge background from a role's accent color.
 */
export function hexToRgba(hex: string, alpha: number): string {
  const normalized = hex.replace("#", "");
  const expanded =
    normalized.length === 3
      ? normalized.split("").map((c) => c + c).join("")
      : normalized;
  const r = parseInt(expanded.slice(0, 2), 16);
  const g = parseInt(expanded.slice(2, 4), 16);
  const b = parseInt(expanded.slice(4, 6), 16);
  if ([r, g, b].some((n) => Number.isNaN(n))) {
    return `rgba(71, 85, 105, ${alpha})`; // fall back to slate (Atom)
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Alpha used for role badge backgrounds (matches the legacy .role-color--* rules). */
export const ROLE_BADGE_BG_ALPHA = 0.12;

/**
 * The bamboo fallback role (managed, with no keyword — i.e. Atom) is the catch-all
 * role. Boards exclude it from their default role filter so non-delivery people
 * aren't shown by default.
 */
export function isBambooFallbackRole(role: Role): boolean {
  return role.is_bamboo_managed && !role.bamboo_keyword;
}

/**
 * Builds the inline style for a role badge/swatch from a role record.
 * Pure version used by the context and by code that already holds a Role.
 */
export function roleColorStyleFor(role: Role | null | undefined): CSSProperties {
  const color = role?.color ?? "#475569"; // slate (Atom) fallback
  return { color, backgroundColor: hexToRgba(color, ROLE_BADGE_BG_ALPHA) };
}
